import React, { useEffect } from 'react';

/**
 * Gemeinsame Optik der beiden Posteingänge (`/admin/anfragen`, `/admin/outreach`).
 *
 * Die Werte stammen aus dem Design-Handoff „Admin — Anfragen"; Outreach ist danach gebaut, weil
 * es dieselbe Arbeit ist: eine Liste links, ein Vorgang rechts, oben die Zahlen. Sie stehen hier
 * und nicht zweimal in den Seiten — zwei Kopien einer Palette laufen beim ersten Nachziehen
 * auseinander, und dann sehen zwei Ansichten desselben Dashboards verschieden aus. Dieselbe
 * Begründung wie bei `components/admin/ui.ts`.
 */

export const FONT_DISPLAY = "'Archivo',sans-serif";
export const FONT_BODY = "'Hanken Grotesk',sans-serif";
export const FONT_MONO = "'Space Mono',monospace";

/** Farbwerte aus dem Design-Handoff. */
export const C = {
  page: '#F5F3F3',
  panel: '#FBFAFA',
  card: '#FFFFFF',
  meta: '#F0EBEB',
  rule: '#E5E1E1',
  ruleSoft: '#F0ECEC',
  ruleSofter: '#EDE9E9',
  cardBorder: '#E9E4E4',
  inputBorder: '#E1DBDB',
  text: '#2C2C2C',
  text2: '#6B5C61',
  text3: '#8A7A80',
  text4: '#A08E95',
  text5: '#A99BA0',
  accent: '#922C57',
  accentHover: '#7C2549',
  accentArea: '#F7E4EC',
  accentBorder: '#E3B9CB',
  warnArea: '#FBF4E4',
  warnBorder: '#EEDDB6',
  warnText: '#7A6018',
  warnSignal: '#D9A93A',
  okArea: '#E4EFE6',
  okText: '#3F6B4A',
  okStripe: '#7FA98A',
};

/**
 * Raster und Zeigerzustände. Als Klassen statt als Inline-Style, weil `:hover` und die
 * Umschaltung auf schmalen Schirmen sich inline nicht ausdrücken lassen.
 *
 * Unter 1000px wird aus den zwei Spalten eine: die Detailspalte legt sich als Vollbild darüber
 * (`data-open`), und der Weg zurück ist die Leiste `.inbox-detail-kopf`. Was nur das Handy
 * (≤768px) betrifft, steht in `App.css` unter `[mobil: Posteingang]`.
 *
 * `min-width: 0` an den Spalten: ohne das darf eine Grid-Spalte so breit werden wie ihre längste
 * nicht umbrechende Zeile, und eine lange Vorschau schob die Liste auf dem Handy 447px über den Rand.
 */
export const INBOX_CSS = `
  .inbox-shell { margin: -12px; }
  .inbox-work { display: grid; grid-template-columns: 352px 1fr; min-height: 0; flex: 1; }
  .inbox-work > * { min-width: 0; }
  .inbox-detail-kopf { display: none; }
  @media (max-width: 1000px) {
    .inbox-work { grid-template-columns: 1fr; }
    .inbox-detail { position: fixed; inset: 0; z-index: 60; background: ${C.page}; }
    .inbox-detail[data-open="false"] { display: none; }
    .inbox-detail-kopf { display: block; margin-bottom: 16px; }
    .inbox-cards { overflow-x: auto; }
  }
  .inbox-row:hover { background: #F4F0F1; }
  .inbox-row[data-active="true"]:hover { background: ${C.accentArea}; }
  .inbox-file:hover { border-color: #D8C6CE !important; }
  .inbox-secondary:hover { border-color: #C9BEC2 !important; }
  .inbox-primary:hover { background: ${C.accentHover} !important; }
  .inbox-tertiary:hover { color: ${C.accent} !important; border-color: ${C.accentBorder} !important; }
`;

/**
 * Die Hülle beider Seiten, zusammen mit der Klasse `inbox-shell`. Volle Fensterhöhe abzüglich
 * `--admin-oben` (Statusleiste) und `--admin-unten` (Tab-Leiste auf dem Handy); das negative
 * margin der Klasse hebt den Innenabstand des Layouts auf, damit der Posteingang bündig bis an
 * die Fensterkanten reicht. Es steht in CSS und nicht hier, weil der Abstand auf dem Handy ein
 * anderer ist.
 */
export const inboxShell: React.CSSProperties = {
  height: 'calc(100dvh - var(--admin-oben, 0px) - var(--admin-unten, 0px))',
  minHeight: 460,
  display: 'flex',
  flexDirection: 'column',
  background: C.page,
};

/**
 * Wie hoch die Bildschirmtastatur gerade ist, als `--inbox-tastatur` am Wurzelelement.
 *
 * iOS Safari verkleinert bei offener Tastatur nicht das Fenster, sondern legt die Tastatur über
 * die Seite: ein Verlauf mit `position: fixed; inset: 0` reicht dann unter sie, und sein Knopf
 * „Senden" liegt dahinter. `visualViewport` kennt den sichtbaren Rest; mit dem Wert als Abstand
 * unten endet die Detailansicht über der Tastatur, und die feste Knopfleiste bleibt erreichbar.
 */
export function useTastaturAbstand() {
  useEffect(() => {
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!vv) return undefined;
    const root = document.documentElement;
    const setzen = () => {
      const abstand = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.setProperty('--inbox-tastatur', `${Math.round(abstand)}px`);
    };
    setzen();
    vv.addEventListener('resize', setzen);
    vv.addEventListener('scroll', setzen);
    return () => {
      vv.removeEventListener('resize', setzen);
      vv.removeEventListener('scroll', setzen);
      root.style.removeProperty('--inbox-tastatur');
    };
  }, []);
}

/**
 * Deterministisch aus dem ganzen Namen, damit dieselbe Person immer dieselbe Kachel hat. Über
 * alle Zeichen gestreut, nicht nur über das erste: im Verlauf stehen Philipp, Roman und der
 * Verein nebeneinander, und mit dem Anfangsbuchstaben allein teilten Roman und „Jan-Peter" eine
 * Farbe.
 */
const AVATARS = [
  { bg: '#F2E2E9', fg: '#922C57' },
  { bg: '#E6E9F0', fg: '#4A5A75' },
  { bg: '#EDE7DE', fg: '#7A6438' },
  { bg: '#E3ECE5', fg: '#456B51' },
];
export const avatarOf = (name: string) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATARS[h % AVATARS.length];
};

/** Heute → Uhrzeit, gestern → „Gestern", älter → Datum. Hält die Liste schmal. */
export const shortTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Gestern';
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
};

export const fullTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('de-DE') : '');

// ── Bausteine der Detailspalte ────────────────────────────────────────────────────────────────
// Sie stehen hier und nicht in den Seiten, weil inzwischen mehrere Dateien desselben Postfachs
// sie brauchen (Seite, Kontaktformular, Kontaktauswahl) — und weil ein Feld, das in einer davon
// anders aussieht, sofort auffaellt.

export const inboxKicker: React.CSSProperties = {
  fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.16em', color: C.text5,
};

export const inboxCard: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: '22px 24px',
};

export const inboxField: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 9,
  border: `1px solid ${C.inputBorder}`, background: C.card,
  fontFamily: FONT_BODY, fontSize: 13.5, color: C.text, outline: 'none',
};

export const inboxFieldLabel: React.CSSProperties = {
  display: 'block', fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: '0.14em',
  color: C.text5, marginBottom: 5,
};

export const inboxPrimaryBtn: React.CSSProperties = {
  background: C.accent, color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 999,
  fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, cursor: 'pointer', textDecoration: 'none',
};

export const inboxSecondaryBtn: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.inputBorder}`, padding: '10px 16px', borderRadius: 999,
  fontFamily: FONT_BODY, fontWeight: 600, fontSize: 13.5, color: '#4A4A4A', cursor: 'pointer',
  textDecoration: 'none',
};
